import React, { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    TableCell as MuiTableCell,
    Button
} from '@mui/material';
import { styled, tableCellClasses } from '@mui/material';
import { PrimaryButton } from '../components';
import InfoHeader from '../components/InfoHeader';
import BasicMenu from '../components/BasicMenu';
import { useAuth } from '../context/AuthContext';
import { colaboradorService } from '../services/ColaboradorService';
import toastService from '../utils/toastService';
import Colaborador from '../types/negocio/Colaborador';
import ColaboradorModal from '../components/modais/ColaboradorModal';
import { departamentoService } from '../services/DepartamentoService';
import { Departamento } from '../types/negocio/Departamento';
import LoadingIndicator from '../components/LoadingIndicator';

const StyledTableCell = styled(MuiTableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: '#042E4B',
        color: theme.palette.common.white,
        fontWeight: 'bold'
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

export default function Colaboradores() {
    const [colaboradores, setColaboradores] = useState<Colaborador[]>({} as Colaborador[]);
    const [departamentos, setDepartamentos] = useState<Departamento[]>({} as Departamento[]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [modalAberto, setModalAberto] = useState(false);
    const [modoEdicao, setModoEdicao] = useState(false);
    const [colaboradorSelecionado, setColaboradorSelecionado] = useState<Colaborador>({} as Colaborador);
    const { user } = useAuth();

    const criarColaboradorVazio = (): Colaborador => ({
        nomeCompleto: '',
        cargoAtual: '',
        departamento: '',
        telefone: '',
        celular: '',
        email: '',
        admissao: new Date().toISOString(),
        formacaoAcademica: '',
        certificacoes: '',
        experienciaProfissional: '',
        treinamentosRealizados: '',
        habilidadesTecnicas: '',
        habilidadesComportamentais: '',
        idiomas: '',
        ativo: true
    });

    useEffect(() => {
        const fetchColaboradores = async () => {
            try {
                setIsLoading(true);
                setError(null);

                const [colaboradoresData, departamentosData] = await Promise.all([
                    colaboradorService.listarTodos(),
                    departamentoService.listarTodos()
                ]);

                setColaboradores(colaboradoresData);
                setDepartamentos(departamentosData);
            } catch (err) {
                console.error("Erro ao carregar colaboradores:", err);
                setError("Não foi possível carregar os dados dos colaboradores. Por favor, tente novamente.");
                toastService.error("Erro ao carregar dados dos colaboradores.");
            } finally {
                setIsLoading(false);
            }
        };

        if (user?.guid) {
            fetchColaboradores();
        }
    }, [user?.guid]);


    const handleEdit = (index: number) => {
        setColaboradorSelecionado(colaboradores[index]);
        setModoEdicao(true);
        setModalAberto(true);
    };

    const handleNovoColaborador = () => {
        setColaboradorSelecionado(criarColaboradorVazio());
        setModoEdicao(true);
        setModalAberto(true);
    };

    const handleViewMore = (index: number) => {
        setColaboradorSelecionado(colaboradores[index]);
        setModoEdicao(false);
        setModalAberto(true);
    };

    const handleSaveColaborador = async (data: Colaborador) => {
        try {
            // Verifica se é um novo colaborador (sem GUID ou GUID vazio) ou edição
            const isNovoColaborador = !data.guid || data.guid.trim() === '';
            
            await colaboradorService.save(data);
            
            toastService.success(isNovoColaborador ? 'Colaborador criado com sucesso!' : 'Colaborador atualizado com sucesso!');
            setModalAberto(false);
            
            // atualizar a lista
            const listaAtualizada = await colaboradorService.listarTodos();
            setColaboradores(listaAtualizada);
        } catch (error) {
            toastService.error('Erro ao salvar colaborador.');
        }
    };


    const handleDelete = async (index: number) => {
        const colaboradorToDelete = colaboradores[index];

        if (!colaboradorToDelete?.guid) {
            toastService.error("Colaborador não encontrado.");
            return;
        }

        try {
            await colaboradorService.deletar(colaboradorToDelete.guid);
            const novaLista = colaboradores.filter((_, i) => i !== index);
            setColaboradores(novaLista);
            toastService.success("Colaborador excluído com sucesso!");
        } catch (error) {
            console.error("Erro ao excluir colaborador:", error);
            toastService.error("Erro ao excluir colaborador. Tente novamente.");
        }
    };


    // Show loading state when data is being fetched
    if (isLoading) {
        return <LoadingIndicator fullHeight message="Carregando colaboradores..." />;
    }

    // Show error state only after loading is complete and there's an error
    if (error && !isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: 2 }}>
                <Typography color="error">{error}</Typography>
                <PrimaryButton onClick={() => window.location.reload()}>
                    Tentar Novamente
                </PrimaryButton>
            </Box>
        );
    }

    return (
        <Box>
            <InfoHeader
                title="Colaboradores"
                message="Gerencie os colaboradores que farão parte do planejamento estratégico da sua empresa."
                isImportant
            />

            <Paper elevation={3} sx={{ mt: 2 }}>
                <TableContainer sx={{ borderRadius: '8px' }}>
                    <Table aria-label="colaboradores table">
                        <TableHead>
                            <TableRow>
                                <StyledTableCell><strong>Nome completo</strong></StyledTableCell>
                                <StyledTableCell><strong>Email</strong></StyledTableCell>
                                <StyledTableCell><strong>Cargo atual</strong></StyledTableCell>
                                <StyledTableCell><strong>Departamento</strong></StyledTableCell>
                                <StyledTableCell align="center"><strong>Ações</strong></StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {colaboradores.map((colab, index) => (
                                <StyledTableRow key={index}>
                                    <StyledTableCell 
                                        component="th" 
                                        scope="row"
                                        onClick={() => handleViewMore(index)}
                                        sx={{ cursor: "pointer" }}
                                    >
                                        <Typography variant="body2" sx={{ fontSize: 16, "&:hover": { textDecoration: "underline", color: "primary.main" } }}>
                                            {colab.nomeCompleto}
                                        </Typography>
                                    </StyledTableCell>
                                    <StyledTableCell component="th" scope="row">
                                        <Typography variant="body2" sx={{ fontSize: 16 }}>
                                            {colab.email}
                                        </Typography>
                                    </StyledTableCell>
                                    <StyledTableCell component="th" scope="row">
                                        <Typography variant="body2" sx={{ fontSize: 16 }}>
                                            {colab.cargoAtual}
                                        </Typography>
                                    </StyledTableCell>
                                    <StyledTableCell component="th" scope="row">
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontSize: 16,
                                                color: colab.departamento && departamentos.find(dep => dep.guid === colab.departamento)
                                                ? 'text.primary'
                                                : 'secondary.main'
                                            }}
                                        >
                                            {departamentos.find(dep => dep.guid === colab.departamento)?.nome || "Sem departamento"}
                                        </Typography>
                                    </StyledTableCell>
                                    <StyledTableCell align="center">
                                        <BasicMenu
                                            onEdit={() => handleEdit(index)}
                                            onDelete={() => handleDelete(index)}
                                            onViewMore={() => handleViewMore(index)}
                                        />
                                    </StyledTableCell>
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                <PrimaryButton variant="contained" onClick={handleNovoColaborador}>
                    Adicionar
                </PrimaryButton>
            </Box>

            <ColaboradorModal
                open={modalAberto}
                onClose={() => setModalAberto(false)}
                colaborador={colaboradorSelecionado}
                departamentos={departamentos}
                modoEdicao={modoEdicao}
                setColaborador={modoEdicao ? setColaboradorSelecionado : undefined}
                onSave={modoEdicao ? handleSaveColaborador : undefined}
                onEditClick={() => setModoEdicao(true)}
                isNovoColaborador={!colaboradorSelecionado?.guid || colaboradorSelecionado.guid.trim() === ''}
            />
        </Box>
    );
}
